import { useFirestore } from "@/hooks/firestore/v2/useFirestore";
import {
  raceMeeting,
  type RaceMeeting,
} from "@/hooks/firestore/v2/models/raceMeeting";
import {
  limit,
  orderBy,
  type QueryConstraint,
  where,
} from "firebase/firestore";
import { useUserEventFilters } from "@/hooks/firestore";
import { useMemo } from "react";
import { useTokens } from "@/hooks/firestore/v2/user/useTokens";
import { useCampaigns } from "@/hooks/firestore/v2/user/useCampaigns";
import { usePermissions } from "@/hooks/useHasPermission";
import {
  processRaceMeeting,
  regionMap,
  sortMeetings,
} from "@/hooks/firestore/v2/betting/shared";
import type { Always } from "@/types/utilities";
import { useRacingEvents } from "@/hooks/firestore/v2/betting/useRacingEvents";
import { endOfDay, isToday, startOfDay } from "date-fns";

type RaceMeetingOptions = {
  startDate?: Date;
  endDate?: Date;
  regions?: ("au" | "world")[];
  titles?: string[];
  status?: RaceMeeting["currentRace"]["status"];
  page?: number;
};

const raceTitleOrder: RaceMeeting["sport"][] = [
  "HORSE_RACING",
  "GREYHOUNDS",
  "HARNESS_RACING",
];

const getRaceMeetingsQuery = ({
  startDate,
  endDate,
  regions,
  titles,
  eventFilters,
  page,
  status,
}: RaceMeetingOptions & { eventFilters: string[] }) => {
  const queries: QueryConstraint[] = [
    where("visible", "==", true),
    orderBy("currentRace.scheduledStartTimeTs", "asc"),
  ];

  const safeRegions = (regions || []).map((region) => regionMap[region]);

  if (startDate) {
    queries.push(where("currentRace.scheduledStartTimeTs", ">=", startDate));
  }

  if (endDate) {
    queries.push(where("currentRace.scheduledStartTimeTs", "<=", endDate));
  }

  if (eventFilters?.length > 0) {
    queries.push(where("filters", "array-contains-any", eventFilters));
  }

  if (safeRegions) {
    queries.push(where("region", "in", safeRegions));
  }

  if (titles && titles.length > 0) {
    queries.push(
      where(
        "sport",
        "in",
        titles.map((title) => title.toUpperCase()),
      ),
    );
  }

  if (page) {
    queries.push(limit(page * 50));
  }

  if (status) {
    queries.push(where("currentRace.status", "==", status));
  }

  return queries;
};

const useRaceMeetings = ({
  startDate,
  endDate,
  regions,
  titles,
  status,
  page,
}: RaceMeetingOptions) => {
  const eventFilters = useUserEventFilters();
  const { tokens } = useTokens();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const {
    data,
    isLoading: meetingsLoading,
    error,
  } = useFirestore(`/raceMeetings`, raceMeeting.array(), {
    keyPath: "id",
    query: getRaceMeetingsQuery({
      startDate,
      endDate,
      regions,
      titles,
      eventFilters,
      status,
      page,
    }),
  });

  const meetings = useMemo(() => {
    if (!data || !codes) return;

    return data.map(
      (meeting) => processRaceMeeting(meeting, codes, tokens, permissions),
      [data],
    );
  }, [data, codes, permissions, tokens]);

  return {
    meetings,
    isLoading: meetingsLoading || codesLoading,
    error,
  };
};

const useGroupedRaceMeetings = (options: RaceMeetingOptions) => {
  const { meetings, isLoading, error } = useRaceMeetings(options);

  const groupedMeetings = useMemo(() => {
    if (!meetings) return;

    const groupedMeetings: Partial<
      Record<RaceMeeting["sport"], ProcessedRaceMeeting[]>
    > = {};

    for (const title of raceTitleOrder) {
      const meetingsByTitle = meetings
        .filter((meeting) => meeting.sport === title)
        .sort(sortMeetings);

      if (meetingsByTitle.length > 0) {
        groupedMeetings[title] = meetingsByTitle;
      }
    }

    return groupedMeetings;
  }, [meetings]);

  return { meetings: groupedMeetings, isLoading, error };
};

const useRaceMeetingsCounts = (options: Omit<RaceMeetingOptions, "titles">) => {
  const { meetings } = useRaceMeetings({
    ...options,
    titles: ["HORSE_RACING", "GREYHOUNDS", "HARNESS_RACING"],
  });

  return useMemo(() => {
    if (!meetings) return {};

    return meetings.reduce<Record<string, number>>((acc, meeting) => {
      const slug = meeting.sport.toLowerCase();

      if (!acc[slug]) {
        acc[slug] = 0;
      }

      acc[slug] += 1;

      return acc;
    }, {});
  }, [meetings]);
};

type ProcessedRaceMeeting = Always<
  ReturnType<typeof useRaceMeetings>["meetings"]
>[0];

const useRaceMeetingWithRaces = (id: string | undefined) => {
  const {
    data,
    error,
    isLoading: meetingsLoading,
  } = useFirestore(id ? `/raceMeetings/${id}` : null, raceMeeting, {
    keyPath: "id",
  });

  const { events: races, isLoading: racesLoading } = useRacingEvents({
    meetingId: id,
  });

  const { tokens } = useTokens();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const meeting = useMemo(() => {
    if (!data || !codes) return;

    return processRaceMeeting(data, codes, tokens, permissions);
  }, [data, codes, tokens, permissions]);
  return {
    meeting,
    races,
    isLoading: meetingsLoading || codesLoading || racesLoading,
    error,
  };
};

const useSuggestedMeetings = (
  date: Date,
  region: ProcessedRaceMeeting["region"],
  sport: string,
) => {
  const isRaceToday = isToday(date);
  const queryRegion = region === "ANZ" ? "au" : "world";

  const options: RaceMeetingOptions = isRaceToday
    ? {
        regions: [queryRegion],
        status: "OPEN",
        page: 1,
      }
    : {
        regions: [queryRegion],
        startDate: startOfDay(date),
        endDate: endOfDay(date),
      };

  const { meetings, isLoading, error } = useRaceMeetings(options);

  const sortedMeetings = useMemo(() => {
    if (!meetings) return;

    return meetings
      .filter((meeting) => meeting.sport === sport)
      .sort((a, b) => {
        if (isRaceToday) {
          return (
            a.currentRace.scheduledStartTimeTs.toDate().getTime() -
            b.currentRace.scheduledStartTimeTs.toDate().getTime()
          );
        }

        return (
          a.scheduledStartTimeTs.toDate().getTime() -
          b.scheduledStartTimeTs.toDate().getTime()
        );
      });
  }, [meetings]);

  return {
    meetings: sortedMeetings,
    isLoading,
    error,
  };
};

export type { ProcessedRaceMeeting };
export {
  useRaceMeetings,
  useGroupedRaceMeetings,
  useRaceMeetingsCounts,
  useRaceMeetingWithRaces,
  useSuggestedMeetings,
};
